import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { loadStripe } from "@stripe/stripe-js";
import { CardElement, Elements, useStripe, useElements } from "@stripe/react-stripe-js";
import { attachePaymentMethod, setupOneClickCharge, startCharging } from "../services/apiService";
import { useErrorHandler } from "../utils/errorHandler";
import { getConnectorPrice, numberToLetter } from "../utils/rateUtils";
import analyticsService from "../services/analyticsService";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const CheckoutForm = () => {
  const { t } = useTranslation();
  const { handleError } = useErrorHandler();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [paymentError, setPaymentError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [showFirstModal, setShowFirstModal] = useState(false);
  const [showSecondModal, setShowSecondModal] = useState(false);
  const [recaptchaScore, setRecaptchaScore] = useState(null);
  const [recaptchaError, setRecaptchaError] = useState("");
  const [processingStep, setProcessingStep] = useState("");
  const [hasExistingToken, setHasExistingToken] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [hasSetupCompleted, setHasSetupCompleted] = useState(false);
  const [attachData, setAttachData] = useState(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { connector, evcpData, reference, partner } = location.state || {};
  const stripe = useStripe();
  const elements = useElements();

  const price = getConnectorPrice(connector, evcpData);

  useEffect(() => {
    const handleBack = () => {
      if (!reference) return;

      const partnerParam = partner ? `?partner=${partner}` : "";
      navigate(`/${reference}${partnerParam}`, { replace: true });
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handleBack);

    return () => {
      window.removeEventListener("popstate", handleBack);
    };
  }, [navigate, reference, partner]);

  useEffect(() => {
    analyticsService.pushPageView('payment_form', {
      station_id: evcpData?.reference,
      connector_id: connector?.reference,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Vérifier si un token existe déjà au chargement du composant
  useEffect(() => {
    const existingToken = sessionStorage.getItem("accessToken");
    if (existingToken) {
      setHasExistingToken(true);
    }
  }, []);

  // Charger le script reCAPTCHA v3
  useEffect(() => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);

    if (value.trim() === "") {
      setEmailError(t("errors.emailRequired"));
    } else if (!validateEmail(value)) {
      setEmailError(t("errors.emailInvalid"));
    } else {
      setEmailError("");
    }
  };

  // Fonction pour exécuter reCAPTCHA v3
  const executeRecaptcha = async (action) => {
    return new Promise((resolve, reject) => {
      if (window.grecaptcha) {
        window.grecaptcha.ready(() => {
          window.grecaptcha
            .execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY, { action })
            .then((token) => {
              resolve(token);
            })
            .catch((error) => {
              const errorMessage = handleError(error);
              setRecaptchaError(errorMessage);
              reject(error);
            });
        });
      } else {
        const error = new Error("reCAPTCHA n'est pas chargé");
        const errorMessage = handleError(error);
        setRecaptchaError(errorMessage);
        reject(error);
      }
    });
  };

  // Empêcher la soumission du formulaire avec la touche Entrée
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      return false;
    }
  };

    // demarrer la charge apres confirmation Stripe
    const startChargingProcess = async (setupDataParam, paymentMethodParam, intentId) => {
      try {
        setProcessingStep(t("processing.startingCharge"));
        await startCharging(evcpData.reference, connector.reference, setupDataParam.accessToken);
        analyticsService.pushEvent('charge_started', {
          station_id: evcpData.reference,
          connector_id: connector.reference,
        });
        navigate("/start-charging", {
          state: {
            paymentMethodId: paymentMethodParam.id,
            accessToken: setupDataParam.accessToken,
            connector,
            evcpData,
            paymentIntentId: intentId,
          },
        });
      } catch (error) {
        console.error("Erreur lors du démarrage de la charge:", error);
        analyticsService.pushEvent('charge_start_failed', {
          station_id: evcpData?.reference,
          connector_id: connector?.reference,
          error_type: error?.response?.status || 'unknown',
        });
        const errorMessage = handleError(error);
        throw new Error(errorMessage);
      }
    };

  const handleSubmit = async (event) => {
    // event.preventDefault();

    // Réinitialiser les erreurs
    setEmailError("");
    setPaymentError("");
    setRecaptchaError("");
    setProcessingStep("");

    if (!stripe || !elements) {
      setPaymentError(t("errors.unknownError"));
      return;
    }

    if (!validateEmail(email)) {
      setEmailError(t("errors.emailInvalid"));
      return;
    }

    analyticsService.pushEvent('payment_email_submitted', { station_id: evcpData?.reference });
    setIsProcessing(true);
    setProcessingStep(t("processing.initializing"));

    try {
      // Si nous avons deja complete la configuration : skip les premières étapes
      if (!hasSetupCompleted) {
        // reCAPTCHA v3 avec l'action "payment"
        setProcessingStep(t("processing.verifying"));
        const recaptchaToken = await executeRecaptcha("payment");

        if (!recaptchaToken) {
          const error = new Error("La vérification reCAPTCHA a échoué");
          const errorMessage = handleError(error);
          setRecaptchaError(errorMessage);
          return;
        }

        const cardElement = elements.getElement(CardElement);

        // Creer un PaymentMethod avec Stripe
        setProcessingStep(t("processing.creatingPayment"));
        const { error: paymentMethodError, paymentMethod: pm } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
          billing_details: {
            email: email,
          },
        });

        if (paymentMethodError) {
          console.error("[Stripe error]", paymentMethodError);
          const errorMessage = handleError(paymentMethodError);
          throw new Error(errorMessage);
        }

        analyticsService.pushEvent('payment_method_created');

        // setup One-Click Charge → { accessToken, refreshToken } only
        setProcessingStep(t("processing.setupPayment"));
        const sd = await setupOneClickCharge(email);
        analyticsService.pushEvent('onecc_setup_success', {
          station_id: evcpData?.reference,
          connector_id: connector?.reference,
        });

        // Stocker les tokens pour la suite
        sessionStorage.setItem("accessToken", sd.accessToken);
        sessionStorage.setItem("refreshToken", sd.refreshToken);

        // Attacher le PM → fingerprint dedup + PI creation → { clientSecret, intentId, status, ... }
        setProcessingStep(t("processing.confirmingPayment"));
        const ad = await attachePaymentMethod(pm.id, sd.accessToken);

        // Confirmer uniquement si le PI n'est pas déjà pré-autorisé
        if (ad.status !== 'requires_capture') {
          if (!ad.clientSecret) {
            throw new Error(t("errors.unknownError"));
          }
          const { error: confirmError } = await stripe.confirmCardPayment(ad.clientSecret, {
            payment_method: pm.id,
          });

          if (confirmError) {
            console.error("[Stripe confirm error]", confirmError);
            const errorMessage = handleError(confirmError);
            throw new Error(errorMessage);
          }
        }

        analyticsService.pushEvent('authorization_confirmed', {
          station_id: evcpData?.reference,
          connector_id: connector?.reference,
        });

        // Stocker les données de setup et le paymentMethod pour les réutiliser si nécessaire
        setSetupData(sd);
        setPaymentMethod(pm);
        setAttachData(ad);
        setHasSetupCompleted(true);

        // demarrer le processus de charge
        await startChargingProcess(sd, pm, ad.intentId);
      } else {
        // Si nous avons déjà les données de setup, nous allons directement au processus de charge
        await startChargingProcess(setupData, paymentMethod, attachData.intentId);
      }

    } catch (error) {
      console.error("Erreur lors du processus de paiement:", error);
      analyticsService.pushEvent('payment_failed', {
        station_id: evcpData?.reference,
        error_type: error.message,
      });
      setPaymentError(error.message);
    } finally {
      setIsProcessing(false);
      setProcessingStep("");
    }
  };

  const handleClick = () => {
    // setShowFirstModal(true);
    handleSubmit();
  };

  const handleFirstModalNext = () => {
    setShowFirstModal(false);
    setShowSecondModal(true);
  };

  const handleChargConfirmation = () => {
    setShowSecondModal(false);
    setShowFirstModal(false);
    handleSubmit();
  };

  const handleSecondModalNo = () => {
    setShowSecondModal(false);
    setShowFirstModal(true);
  };

  const closeModal = () => {
    setShowFirstModal(false);
    setShowSecondModal(false);
  };

  const CARD_ELEMENT_OPTIONS = {
    style: {
      base: {
        color: "#37373B",
        fontSize: "16px",
        fontWeight: "400",
        backgroundColor: "rgba(91, 62, 255, 0.06)",
        borderBottom: "1px solid #5b3eff",
        padding: "8px 16px",
        outline: "none",
      },
      focus: {
        borderBottom: "1px solid #5b3eff",
      },
      invalid: {
        borderBottom: "1px solid red",
      },
    },
  };

  return (
    <div className="bg-white flex flex-col items-center justify-center p-4 pt-32 md:pt-36">
      <div className="w-full max-w-2xl space-y-6 pb-75">
        {/* Section de récapitulatif du connecteur */}
        <div className="rounded-xl border border-[#3f4a80]">
          <div className="details-header">{t("recapitulation")}</div>
          <div className="px-4 py-3 text-sm text-gray-800 space-y-3">
            {/* Adresse */}
            {/* <div className="w-full space-y-3">
              <div className="flex items-center gap-2">
                <img src="/Map_Point.svg" className="w-4 h-4" alt="location" />
                <span>{evcpData?.address}</span>
              </div>
              <div className="w-full border-gray-400"></div>
            </div> */}
            
            {/* Connecteur sélectionné */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img src="/Plug_Circle.svg" className="w-4 h-4" alt="connecteur" />
                <span>{t("connector", { number: numberToLetter(connector?.number) })}</span>
                
              </div>
              <span className="font-semibold">⚡ {Math.floor(connector?.powerMax || 0)} kW</span>
            </div>
            
            {/* Prix */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img src="/Euro.svg" className="w-4 h-4" alt="prix" />
                <span>{t("Tarif")}</span>
              </div>
              <span className="font-semibold">{price} €/kWh</span>
            </div>
          </div>
        </div>

        <div className="alert flex items-start gap-1 mb-6">
          <img src="/Danger_Circle.svg" alt="Danger icon" className="icon mr-2" />
          <h2 className="text-info-message">
            {t("plugBeforePayment")}
          </h2>
        </div>
        <div className="alert flex items-start gap-1 mb-6">
          <img src="/Shield_Check.svg" alt="Lock icon" className="icon mr-2" />
          <p className="text-info-message">{t("securePayment")}</p>
        </div>

        {/* erreurs générales */}
        {paymentError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
            <span className="block sm:inline">{paymentError}</span>
          </div>
        )}

        {/* Indicateur de progression */}
        {isProcessing && processingStep && (
          <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded relative" role="status">
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700 mr-2"></div>
              <span className="block sm:inline">{processingStep}</span>
            </div>
          </div>
        )}

        <div className="space-y-4" onKeyDown={handleKeyDown}>
          <div className="text-input-group mt-4 w-full">
            <label className="text-label">{t("emailLabel")}</label>
            <input
              type="email"
              className={`styled-input ${emailError ? "border-red-500" : ""}`}
              value={email}
              onChange={handleEmailChange}
              onKeyDown={handleKeyDown}
              required
            />
            {emailError && <p className="text-red-500 text-xs mt-1">{emailError}</p>}
            <p className="email-help-message">
              {t("emailHelp")}
            </p>
          </div>

          <div className="text-input-group mt-4 w-full">
            <label className="text-label">{t("cardInformation")}</label>
            <div className="styled-input">
              <CardElement options={CARD_ELEMENT_OPTIONS} />
            </div>
          </div>

          {recaptchaError && <p className="text-red-500 text-xs mt-1">{recaptchaError}</p>}

          <button
            className="payment-button"
            type="button"
            onClick={handleClick}
            disabled={!stripe || isProcessing}
          >
            {isProcessing ? (
              <div className="flex justify-center space-x-1">
                {[...Array(3)].map((_, i) => (
                  <span
                    key={i}
                    className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce"
                    style={{ animationDelay: `${i * 0.1}s` }}
                  />
                ))}
              </div>
            ) : (
              t("preAuthButton")
            )}
          </button>
        </div>

        <p className="text-sm-regular mt-6">
          <Trans i18nKey="preAuthNotice">
            <span className="text-sm-semibold">pré-autorisation de 40 €</span> sera temporairement bloquée.
            Seul le montant exact de votre recharge sera prélevé. Le reste sera libéré sous 72h ouvrées.
          </Trans>
        </p>

        {/* la charge s'arrête à 40€ */}
        <div className="mt-2 p-3 bg-amber-50 border-l-4 border-amber-500 rounded-r">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-amber-500 mt-0 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-amber-800 font-medium">
              {t("chargeLimitNotice")}
            </p>
          </div>
        </div>

      </div>

      {/* Modal : Branchez votre véhicule */}
      {/* {showFirstModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full text-center">
            <img
              src="/charging-car.png"
              alt="Branchez votre véhicule"
              className="mx-auto mb-4 w-48 h-48"
            />
            <h3 className="text-lg font-semibold mb-4">{t("modalTitle")}</h3>
            <div className="flex justify-center gap-4">
              <button
                onClick={handleFirstModalNext}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded"
              >
                {t("no")}
              </button>
              <button
                onClick={handleChargConfirmation}
                className="px-4 py-2 bg-indigo-600 text-white rounded"
              >
                {t("yes")}
              </button>
            </div>
          </div>
        </div>
      )} */}

      {/* Modal : vous devez brancher votre vehicule */}
      {showSecondModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full text-center">
            <img
              src="/charging-car.png"
              alt="Branchez votre véhicule"
              className="mx-auto mb-4 w-48 h-48"
            />
            <h3 className="text-lg font-semibold mb-4">{t("confirmationModalTitle")}</h3>
            <div className="flex justify-center gap-4">
              <button
                onClick={handleSecondModalNo}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded"
              >
                {t("notYet")}
              </button>
              <button
                onClick={handleChargConfirmation}
                className="px-4 py-2 bg-indigo-600 text-white rounded"
              >
                {t("done")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StripeForm = () => {
  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm />
    </Elements>
  );
};

export default StripeForm;